import { ChangeEvent, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	DocumentTextOutline,
	ExclamationCircleSolid,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	HStack,
	IconButton,
	InformationCircleSolid,
	Input,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	PlusOutline,
	rem,
	Text,
	TrashOutline,
	VStack,
} from '@bonusly/design-system';

import { ReactComponent as Bubbles } from '../../../../assets/images/loading-bubbles.svg';
import CsvUploadIcon from '../../../../entities/recipient/icon-group-csv-upload.svg';
import { CountHelpText } from '../../../form/count-help-text';
import { useActionInput } from '../../context';
import { useUsersByEmail } from '../../hooks/use-users-by-email';

interface RecipientCSVModalProps {
	csvEmailAddresses: string[];
	fileName: string;
	handleCSVChange: (event: ChangeEvent<HTMLInputElement>) => void;
	handleDeleteFile: () => void;
	isOpen: boolean;
	onClose: () => void;
}

interface RecipientCsvData {
	name: string;
}

export const RecipientCSVModal = ({ fileName, isOpen, csvEmailAddresses, onClose, handleCSVChange, handleDeleteFile }: RecipientCSVModalProps) => {
	const { t } = useTranslation('components', { keyPrefix: 'rich_media' });
	const { data, isLoading } = useUsersByEmail(csvEmailAddresses);
	const userIds = data?.user_ids || [];
	const invalidEmails = data?.emails_not_found || [];
	const hasInvalidEmails = invalidEmails.length > 0;

	const form = useForm<RecipientCsvData>({
		defaultValues: {
			name: '',
		},
	});
	const { handleSubmit, register, watch, setValue } = form;
	const groupName = watch('name');

	const { editor } = useActionInput();

	const addGroup = (data: RecipientCsvData) => {
		// NOTE: can't add group if editor is not available
		if (!editor) return;

		// NOTE: remove anything other than letters, numbers and spaces, replace spaces with dots, replace any multiple dots with a single dot, make the group name lowercase
		const username = data.name
			.replace(/[^a-zA-Z0-9\s]/g, '')
			.replace(/\s/g, '.')
			.replace(/\.+/g, '.')
			.toLowerCase();
		const content = [
			{
				type: 'recipient',
				attrs: {
					id: username,
					record: {
						dimension: 'csv_upload',
						displayName: data.name,
						id: username,
						imageSrc: CsvUploadIcon,
						type: 'group',
						userIds: userIds,
						username,
					},
				},
			},
			{ type: 'text', text: ' ' },
		];

		if (editor) {
			editor.commands.setContent(content);
		}
		onClose();
	};

	const handleCSVUploadClick = useCallback(() => {
		document.getElementById('modal-csv-uploader')?.click();
	}, []);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="2xl">
			<FormProvider {...form}>
				<ModalContent as="form" onSubmit={handleSubmit(addGroup)}>
					<ModalHeader>{t('uploadCsvModal.title')}</ModalHeader>
					<ModalBody p={5}>
						{isLoading ? (
							<VStack alignItems="center" my={24}>
								<Bubbles height={rem(150)} />
								<Text bottom={rem(40)} fontSize={50} position="relative" textAlign="center">
									{t('uploadCsvModal.processingCsv')}
								</Text>
							</VStack>
						) : (
							<>
								<Text fontSize={50} fontWeight="medium" mb={1}>
									{t('uploadCsvModal.uploadedCsv')}
								</Text>
								{fileName ? (
									<>
										<HStack bgColor="info.bg" p={2} borderRadius="md" mb={1}>
											<InformationCircleSolid />
											<Text fontSize={50}>{t('uploadCsvModal.recipient', { count: userIds.length })}</Text>
										</HStack>
										<Flex
											h="fit-content"
											p={4}
											w="100%"
											alignItems="center"
											justifyContent="space-between"
											borderColor={hasInvalidEmails ? 'error.inputBorder' : 'border.input'}
											borderRadius="md"
											borderWidth={rem(1)}
											backgroundColor="bg.surface"
											color="text.default"
										>
											<Flex alignItems="center" w="90%">
												<DocumentTextOutline size="md" />
												<Text color="text.default" fontSize={50} fontWeight="medium" lineHeight={5} ml={2} overflow="hidden">
													{fileName}
												</Text>
											</Flex>
											<IconButton
												aria-label={t('uploadCsvModal.deleteFile')}
												data-testid="delete-file-button"
												onClick={handleDeleteFile}
												colorScheme="secondary"
												variant="ghost"
												borderRadius="full"
												_hover={{
													bgColor: 'secondary.bgAlpha',
												}}
											>
												<TrashOutline />
											</IconButton>
										</Flex>
										{hasInvalidEmails && (
											<Flex mt={1}>
												<ExclamationCircleSolid color="error.text" size="sm" mr={1} />
												<Box>
													<Text color="error.text" fontSize={50} fontWeight="medium">
														{t('uploadCsvModal.invalidEmails', { count: invalidEmails.length, emails: invalidEmails.join(', ') })}
													</Text>
													<Text color="error.text" fontSize={50}>
														{t('uploadCsvModal.invalidEmailsNextSteps')}
													</Text>
												</Box>
											</Flex>
										)}
									</>
								) : (
									<>
										<Button
											variant="outline"
											colorScheme="secondary"
											h="fit-content"
											p={4}
											mb={2}
											w="100%"
											onClick={handleCSVUploadClick}
											justifyContent="flex-start"
											_hover={{ background: 'bg.surface' }}
										>
											<Flex backgroundColor="secondary.bgAlpha" borderRadius="full" p={3} h={10} w={10} alignItems="center" justifyContent="center">
												<PlusOutline />
											</Flex>
											<VStack spacing={0} textAlign="left" ml={3}>
												<Text fontSize={50} fontWeight="medium" w="100%">
													{t('uploadCsv.title')}
												</Text>
												<Text fontSize={50} color="text.softer" w="100%">
													{t('uploadCsv.description')}
												</Text>
											</VStack>
										</Button>
										<Input id="modal-csv-uploader" accept="text/csv" display="none" type="file" onChange={handleCSVChange} />
									</>
								)}
								<FormControl mt={4}>
									<FormLabel fontSize={50}>{t('uploadCsvModal.groupName')}</FormLabel>
									<Input
										placeholder={t('uploadCsvModal.groupNamePlaceholder')}
										maxLength={40}
										borderColor="border.input"
										fontSize={50}
										_hover={{ borderColor: 'border.input' }}
										_focus={{
											borderColor: 'border.input',
											boxShadow: 'none',
										}}
										{...register('name', {
											onChange: (e) => {
												// NOTE: we don't allow @ # + or & in the group name
												const sanitizedValue = e.target.value.replace(/[@#&+\.]/g, '');
												setValue('name', sanitizedValue);
											},
										})}
									/>
									<HStack justifyContent="space-between" alignItems="flex-start">
										<FormHelperText width="87%">{t('uploadCsvModal.groupNameHint')}</FormHelperText>
										<CountHelpText count={groupName.length} max={40} />
									</HStack>
								</FormControl>
							</>
						)}
					</ModalBody>
					<ModalFooter flexDirection="column" borderTopColor="border.default" borderTopStyle="solid" borderTopWidth="1px">
						<Flex justify="space-between" w="100%">
							<Button colorScheme="secondary" variant="alpha" onClick={onClose}>
								{t('uploadCsvModal.cancel')}
							</Button>
							<Button colorScheme="primary" isDisabled={!groupName || userIds.length === 0 || hasInvalidEmails} type="submit">
								{t('uploadCsvModal.insertGroup')}
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</FormProvider>
		</Modal>
	);
};
